import { useState } from "react"
import { IconCalendarOff, IconLoader2, IconPlus, IconRefresh, IconTrash } from "@tabler/icons-react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import { useGetCalUsersQuery, useGetTeamsQuery } from "@/app/booking-calendar/bookingCalendarApi"
import { todayInZone } from "@/app/booking-calendar/lib/siteTime"
import {
  announceWithdrawal,
  announceWithdrawalFailure,
  OverrideDetailDialog,
} from "../components/OverrideDetailDialog"
import {
  type OverrideStatus,
  type ScheduleOverrideSummary,
  useGetScheduleOverridesQuery,
  useRevokeScheduleOverrideMutation,
} from "./scheduleOverridesApi"
import { NewOverrideDialog } from "./components/NewOverrideDialog"
import { dayCount, describeEffect, describeTarget, formatWindow, kindLabel, STATUS_LABELS } from "./lib/overrideForm"

// The detail dialog and its affected-bookings section are shared with the availability week (AB#803).
export { AffectedBookingsSection } from "../components/OverrideDetailDialog"

const STATUS_VARIANTS: Record<OverrideStatus, "default" | "secondary" | "destructive" | "outline"> = {
  0: "outline",
  1: "default",
  2: "destructive",
  3: "secondary",
  4: "destructive",
  5: "destructive",
}

export default function ScheduleOverridesPage() {
  const { permissions } = useAppSelector((state) => state.permissions)
  const canManage = hasPermission(permissions, "appointments:manage")
  const weekViewOn = useAppSelector((state) => state.features?.features.amsAvailabilityWeekView ?? false)

  const [includePast, setIncludePast] = useState(false)
  const [includeRevoked, setIncludeRevoked] = useState(false)
  const [creating, setCreating] = useState(false)
  const [withdrawing, setWithdrawing] = useState<ScheduleOverrideSummary | null>(null)
  const [viewingId, setViewingId] = useState<string | null>(null)

  const { data, isLoading, isFetching, error, refetch } = useGetScheduleOverridesQuery({ includePast, includeRevoked })

  // Only an administrator creates overrides, so only they need the pickers.
  const { data: teams = [] } = useGetTeamsQuery(undefined, { skip: !canManage })
  const { data: hosts = [] } = useGetCalUsersQuery(undefined, { skip: !canManage })

  const [revoke, { isLoading: revoking }] = useRevokeScheduleOverrideMutation()

  const today = todayInZone()
  const rows = data?.overrides ?? []

  async function handleWithdraw() {
    if (!withdrawing) return

    try {
      announceWithdrawal(await revoke(withdrawing.id).unwrap())
    } catch (err) {
      announceWithdrawalFailure(err)
    } finally {
      setWithdrawing(null)
    }
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconCalendarOff className="size-6" />
          <h1 className="text-2xl font-bold">Schedule Overrides</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            {isFetching ? <IconLoader2 className="size-4 animate-spin" /> : <IconRefresh className="size-4" />}
            Refresh
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setCreating(true)}>
              <IconPlus className="size-4" />
              New override
            </Button>
          )}
        </div>
      </div>

      <p className="text-muted-foreground max-w-3xl">
        Outreach assignments, out-of-office days, site closures and event-type swaps. Each one takes effect
        straight away, so the calendar and public booking follow it at once; withdrawing it puts those days back
        and leaves alone anything that has changed them since.
        {data?.restricted && " You are seeing your own overrides and those at sites you host."}
      </p>

      <div className="flex flex-wrap items-center gap-6">
        <Label className="flex items-center gap-2 font-normal">
          <Checkbox checked={includePast} onCheckedChange={(checked) => setIncludePast(checked === true)} />
          Show past
        </Label>
        <Label className="flex items-center gap-2 font-normal">
          <Checkbox checked={includeRevoked} onCheckedChange={(checked) => setIncludeRevoked(checked === true)} />
          Show withdrawn
        </Label>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
          <p className="text-sm text-destructive">Could not load schedule overrides.</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-muted-foreground">
          {includePast || includeRevoked ? "No overrides match." : "No current or upcoming overrides."}
        </div>
      ) : (
        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>When</TableHead>
                <TableHead>Who / where</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const effect = describeEffect(row)
                const days = dayCount(row.startDate, row.endDate)

                return (
                  <TableRow key={row.id} className="cursor-pointer" onClick={() => setViewingId(row.id)}>
                    <TableCell>
                      <Badge variant="outline">{kindLabel(row.kind, weekViewOn)}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium whitespace-nowrap">
                        {formatWindow(row.startDate, row.startTime, row.endDate, row.endTime)}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {days} {days === 1 ? "day" : "days"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{describeTarget(row)}</div>
                      {effect && <div className="text-sm text-muted-foreground">{effect}</div>}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="text-sm truncate" title={row.note ?? undefined}>
                        {row.note ?? <span className="text-muted-foreground">—</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">by {row.createdBy}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANTS[row.status]}>{STATUS_LABELS[row.status]}</Badge>
                      {(row.failedDays > 0 || row.conflictedDays > 0) && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {row.failedDays > 0 && `${row.failedDays} failed`}
                          {row.failedDays > 0 && row.conflictedDays > 0 && " · "}
                          {row.conflictedDays > 0 && `${row.conflictedDays} changed since`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {canManage && row.status !== 3 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          title="Withdraw"
                          onClick={(e) => {
                            e.stopPropagation()
                            setWithdrawing(row)
                          }}
                        >
                          <IconTrash className="size-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {canManage && (
        <NewOverrideDialog open={creating} onOpenChange={setCreating} teams={teams} hosts={hosts} today={today} />
      )}

      <OverrideDetailDialog id={viewingId} onClose={() => setViewingId(null)} canManage={canManage} />

      <AlertDialog open={withdrawing !== null} onOpenChange={(open) => !open && setWithdrawing(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw this override?</AlertDialogTitle>
            <AlertDialogDescription>
              {withdrawing && (
                <>
                  {kindLabel(withdrawing.kind, weekViewOn)} — {describeTarget(withdrawing)},{" "}
                  {formatWindow(withdrawing.startDate, withdrawing.startTime, withdrawing.endDate, withdrawing.endTime)}. The
                  days it changed go back to what
                  they held before. Days changed by something else since are left alone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={revoking}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleWithdraw()
              }}
              disabled={revoking}
            >
              {revoking && <IconLoader2 className="size-4 animate-spin" />}
              Withdraw
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
